// Baselayer Background Checks — Results Explorer add-ons transport.
// Typed helpers over the /api/background-checks/{id}/* add-on endpoints.
// Hooks in @/hooks/backgroundChecks/useAddOns wrap these with TanStack Query.

import { api } from "../client";
import { downloadBlob } from "../reports";

// ---- wire types ------------------------------------------------------

export type BackgroundCheckAddOnType =
  | "Liens"
  | "Litigations"
  | "Bankruptcies"
  | "WebsiteAnalysis"
  | "ReviewsSocials"
  | "DocketDetail";

export type BackgroundCheckAddOnStatus =
  | "Submitted"
  | "Processing"
  | "Completed"
  | "Failed";

export interface BackgroundCheckAddOnDto {
  id: string;
  backgroundCheckId: string;
  addOnType: BackgroundCheckAddOnType;
  status: BackgroundCheckAddOnStatus;
  baselayerRequestId: string | null;
  orderedByUserId: string | null;
  orderedAt: string;
  isRefresh: boolean;
  optionsJson: string | null;
  rawResponseJson: string | null;
  failureReason: string | null;
}

export interface OrderLiensRequest {
  states?: string[];
  includeOfficers?: boolean;
  officerIds?: string[];
}

export interface OrderLitigationsRequest {
  includeOfficers?: boolean;
}

export interface OrderDocketDetailRequest {
  caseId: string;
}

// ---- transport -------------------------------------------------------

export const addOnsApi = {
  list: (checkId: string) =>
    api.get<BackgroundCheckAddOnDto[]>(
      `/background-checks/${checkId}/add-ons`,
    ),

  orderLiens: (checkId: string, body?: OrderLiensRequest) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-liens`,
      body ?? {},
    ),

  orderLitigations: (checkId: string, body?: OrderLitigationsRequest) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-litigations`,
      body ?? {},
    ),

  orderBankruptcies: (checkId: string) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-bankruptcies`,
      undefined,
    ),

  orderWebsiteAnalysis: (checkId: string) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-website-analysis`,
      undefined,
    ),

  orderReviewsSocials: (checkId: string) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-reviews-socials`,
      undefined,
    ),

  orderDocketDetail: (checkId: string, body: OrderDocketDetailRequest) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/order-docket-detail`,
      body,
    ),

  refresh: (checkId: string, addonType: BackgroundCheckAddOnType) =>
    api.post<BackgroundCheckAddOnDto>(
      `/background-checks/${checkId}/refresh/${addonType}`,
      undefined,
    ),

  /** Downloads the native lien-filing PDF and triggers a browser download. */
  downloadLienDocument: async (checkId: string, lienId: string) => {
    const blob = await api.getBlob(
      `/background-checks/${checkId}/liens/${lienId}/document`,
    );
    downloadBlob(blob, `lien-${lienId}.pdf`);
  },
};
